import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import Modal from '../ui/Modal';
import { blogService, BlogPost, BlogConnection, SeoAnalysis, SeoCheck } from '../../services/blogService';
import { Send, CheckCircle, AlertTriangle, XCircle, Loader2, Wand2, Sparkles } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import ImageUpload from './ImageUpload';
import { imageStudioService } from '../../services/imageStudioService';

// ==================== Helpers ====================

type EditorStatus = 'draft' | 'scheduled';

type TFunc = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

function kindOptions(t: TFunc) {
  return [
    { value: 'article', label: t('blogSeo.posts.kindArticle', 'Artigo') },
    { value: 'news', label: t('blogSeo.posts.kindNews', 'Notícia') },
    { value: 'tutorial', label: t('blogSeo.posts.kindTutorial', 'Tutorial') },
    { value: 'listicle', label: t('blogSeo.posts.kindListicle', 'Lista') },
    { value: 'review', label: t('blogSeo.posts.kindReview', 'Review') },
  ];
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-500';
  if (score >= 50) return 'text-yellow-500';
  return 'text-red-500';
}

function scoreRing(score: number): string {
  if (score >= 80) return 'border-green-500';
  if (score >= 50) return 'border-yellow-500';
  return 'border-red-500';
}

// ==================== SEO Check item ====================

const SeoCheckItem: React.FC<{ check: SeoCheck }> = ({ check }) => {
  const icon =
    check.status === 'good' ? (
      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
    ) : check.status === 'warning' ? (
      <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
    ) : (
      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
    );
  return (
    <div className="flex items-start gap-2 py-1.5">
      {icon}
      <div className="min-w-0">
        <p className="text-sm text-gray-900 dark:text-white">{check.label}</p>
        {check.hint && <p className="text-xs text-gray-500 dark:text-gray-400">{check.hint}</p>}
      </div>
    </div>
  );
};

// ==================== Score circle ====================

const ScoreCircle: React.FC<{ score: number; size?: number }> = ({ score, size = 96 }) => {
  return (
    <div
      className={`rounded-full border-4 ${scoreRing(score)} flex items-center justify-center`}
      style={{ width: size, height: size }}
    >
      <div className="text-center">
        <div className={`text-2xl font-bold ${scoreColor(score)}`}>{score}</div>
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">SEO</div>
      </div>
    </div>
  );
};

// ==================== Editor ====================

interface EditorState {
  title: string;
  slug: string;
  kind: BlogPost['kind'];
  content: string;
  excerpt: string;
  featured_image_url: string;
  categories: string;
  tags: string;
  connection_id: string;
  scheduled_at: string;
  metaTitle: string;
  metaDescription: string;
  focusKeyword: string;
}

const emptyEditor: EditorState = {
  title: '',
  slug: '',
  kind: 'article',
  content: '',
  excerpt: '',
  featured_image_url: '',
  categories: '',
  tags: '',
  connection_id: '',
  scheduled_at: '',
  metaTitle: '',
  metaDescription: '',
  focusKeyword: '',
};

function toDatetimeLocal(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

interface BlogPostEditorProps {
  isOpen: boolean;
  onClose: () => void;
  post: BlogPost | null;
  connections: BlogConnection[];
  onSaved: () => void;
}

const BlogPostEditor: React.FC<BlogPostEditorProps> = ({ isOpen, onClose, post, connections, onSaved }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<EditorState>(emptyEditor);
  const [slugTouched, setSlugTouched] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [analysis, setAnalysis] = useState<SeoAnalysis | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiWriting, setAiWriting] = useState(false);
  const [genImg, setGenImg] = useState(false);

  // Hydrate form when opening / changing post
  useEffect(() => {
    if (!isOpen) return;
    if (post) {
      setForm({
        title: post.title ?? '',
        slug: post.slug ?? '',
        kind: post.kind ?? 'article',
        content: post.content ?? '',
        excerpt: post.excerpt ?? '',
        featured_image_url: post.featured_image_url ?? '',
        categories: (post.categories ?? []).join(', '),
        tags: (post.tags ?? []).join(', '),
        connection_id: post.connection_id ?? '',
        scheduled_at: toDatetimeLocal(post.scheduled_at),
        metaTitle: post.seo?.metaTitle ?? '',
        metaDescription: post.seo?.metaDescription ?? '',
        focusKeyword: post.seo?.focusKeyword ?? '',
      });
      setSlugTouched(Boolean(post.slug));
      setScheduleEnabled(post.status === 'scheduled');
    } else {
      setForm(emptyEditor);
      setSlugTouched(false);
      setScheduleEnabled(false);
    }
    setAnalysis(null);
  }, [isOpen, post]);

  const update = useCallback(<K extends keyof EditorState>(key: K, value: EditorState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Auto-suggest slug from title until user edits slug manually
  const onTitleChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      title: value,
      slug: slugTouched ? prev.slug : slugify(value),
    }));
  };

  // Live SEO analysis (debounced 600ms)
  useEffect(() => {
    if (!isOpen) return;
    if (!form.title.trim() && !form.content.trim()) { setAnalysis(null); return; }
    const handle = setTimeout(async () => {
      try {
        setAnalyzing(true);
        const res = await blogService.analyzeSeo({
          title: form.title,
          content: form.content,
          excerpt: form.excerpt,
          slug: form.slug,
          featured_image_url: form.featured_image_url || undefined,
          seo: {
            metaTitle: form.metaTitle,
            metaDescription: form.metaDescription,
            focusKeyword: form.focusKeyword,
          },
        });
        setAnalysis(res?.data ?? null);
      } catch {
        // silent: live analysis is best-effort
      } finally {
        setAnalyzing(false);
      }
    }, 600);
    return () => clearTimeout(handle);
  }, [
    isOpen,
    form.title,
    form.content,
    form.excerpt,
    form.slug,
    form.featured_image_url,
    form.metaTitle,
    form.metaDescription,
    form.focusKeyword,
  ]);

  const buildPayload = useCallback((): Partial<BlogPost> => {
    const isScheduled = scheduleEnabled && Boolean(form.scheduled_at);
    const status: EditorStatus = isScheduled ? 'scheduled' : 'draft';
    const payload: Partial<BlogPost> = {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      kind: form.kind,
      content: form.content,
      excerpt: form.excerpt.trim() || undefined,
      featured_image_url: form.featured_image_url.trim() || undefined,
      categories: splitCsv(form.categories),
      tags: splitCsv(form.tags),
      connection_id: form.connection_id || null,
      status,
      scheduled_at: isScheduled ? new Date(form.scheduled_at).toISOString() : null,
      seo: {
        metaTitle: form.metaTitle.trim() || undefined,
        metaDescription: form.metaDescription.trim() || undefined,
        focusKeyword: form.focusKeyword.trim() || undefined,
      },
    };
    return payload;
  }, [form, scheduleEnabled]);

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error(t('blogSeo.posts.toastTitleRequired', 'Informe um título'));
      return;
    }
    if (scheduleEnabled && !form.scheduled_at) {
      toast.error(t('blogSeo.posts.toastScheduleDateRequired', 'Informe a data de agendamento'));
      return;
    }
    if (scheduleEnabled && form.scheduled_at && !form.connection_id) {
      toast.error(t('blogSeo.posts.toastConnectionRequired', 'Selecione uma conexão para publicar'));
      return;
    }
    try {
      setSaving(true);
      const payload = buildPayload();
      if (post) {
        await blogService.updatePost(post.id, payload);
        toast.success(t('blogSeo.posts.toastPostUpdated', 'Post atualizado'));
      } else {
        await blogService.createPost(payload);
        toast.success(t('blogSeo.posts.toastPostCreated', 'Post criado'));
      }
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error((err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message) || t('blogSeo.posts.toastSaveError', 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };

  const handlePublishNow = async () => {
    if (!post) {
      toast.error(t('blogSeo.posts.toastSaveBeforePublish', 'Salve o post antes de publicar'));
      return;
    }
    if (!form.connection_id) {
      toast.error(t('blogSeo.posts.toastConnectionRequired', 'Selecione uma conexão para publicar'));
      return;
    }
    try {
      setPublishing(true);
      // Persist current edits first so we publish what is on screen
      await blogService.updatePost(post.id, buildPayload());
      await blogService.publishPost(post.id, form.connection_id);
      toast.success(t('blogSeo.posts.toastPublishStarted', 'Publicação iniciada'));
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error((err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message) || t('blogSeo.posts.toastPublishError', 'Erro ao publicar'));
    } finally {
      setPublishing(false);
    }
  };

  const handleAiWrite = async () => {
    const topic = form.title.trim() || form.focusKeyword.trim();
    if (!topic) {
      toast.error(t('blogSeo.posts.toastAiTopicRequired', 'Escreva um título ou palavra-chave foco para a IA usar como tema'));
      return;
    }
    try {
      setAiWriting(true);
      const res = await blogService.aiWrite({ topic, kind: form.kind });
      const a = res?.data;
      if (!a) throw new Error(t('blogSeo.posts.errorEmptyResponse', 'Resposta vazia'));
      setForm((prev) => ({
        ...prev,
        title: prev.title || a.title || '',
        slug: prev.slug || a.slug || '',
        content: a.content || prev.content,
        excerpt: a.excerpt || prev.excerpt,
        categories: prev.categories || (a.categories || []).join(', '),
        tags: prev.tags || (a.tags || []).join(', '),
        metaTitle: a.seo?.metaTitle || prev.metaTitle,
        metaDescription: a.seo?.metaDescription || prev.metaDescription,
        focusKeyword: prev.focusKeyword || a.seo?.focusKeyword || '',
      }));
      toast.success(t('blogSeo.posts.toastAiGenerated', 'Artigo gerado pela IA ✨'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.posts.toastAiError', 'Erro ao escrever com IA'));
    } finally {
      setAiWriting(false);
    }
  };

  const handleGenerateImage = async () => {
    try {
      setGenImg(true);
      const prompt = `${form.title.trim() || t('blogSeo.posts.imagePromptFallback', 'artigo de blog')} — ${t('blogSeo.posts.imagePromptSuffix', 'imagem de capa profissional, sem texto')}`;
      const res = await imageStudioService.generateImage({ prompt, size: '16:9' });
      const url = res?.data?.original_url;
      if (url) {
        update('featured_image_url', url);
        toast.success(t('blogSeo.posts.imageGenerated', 'Imagem gerada!'));
      } else {
        toast.error(t('blogSeo.posts.toastImageError', 'Erro ao gerar imagem'));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.posts.toastImageError', 'Erro ao gerar imagem'));
    } finally {
      setGenImg(false);
    }
  };

  const connectionOptions = [
    { value: '', label: t('blogSeo.posts.connectionNone', 'Nenhuma (rascunho)') },
    ...connections.map((c) => ({ value: c.id, label: c.name })),
  ];

  const metaTitleLen = form.metaTitle.length;
  const metaDescLen = form.metaDescription.length;
  const metaTitleOk = metaTitleLen >= 30 && metaTitleLen <= 65;
  const metaDescOk = metaDescLen >= 120 && metaDescLen <= 160;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={post ? t('blogSeo.posts.editPostTitle', 'Editar post') : t('blogSeo.posts.newPostTitle', 'Novo post')} size="3xl" maxHeight>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* LEFT: main content */}
        <div className="md:col-span-2 space-y-4">
          <Input label={t('blogSeo.posts.titleLabel', 'Título')} value={form.title} onChange={(e) => onTitleChange(e.target.value)} placeholder={t('blogSeo.posts.titlePlaceholder', 'Título do post')} />
          <Input
            label={t('blogSeo.posts.slugLabel', 'Slug')}
            value={form.slug}
            onChange={(e) => {
              setSlugTouched(true);
              update('slug', e.target.value);
            }}
            placeholder={t('blogSeo.posts.slugPlaceholder', 'meu-post-incrivel')}
          />
          <Select
            label={t('blogSeo.posts.kindLabel', 'Tipo')}
            options={kindOptions(t)}
            value={form.kind}
            onChange={(e) => update('kind', e.target.value as BlogPost['kind'])}
          />
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label mb-0">{t('blogSeo.posts.contentLabel', 'Conteúdo')}</label>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Wand2 className="w-3.5 h-3.5" />}
                onClick={handleAiWrite}
                isLoading={aiWriting}
              >
                {t('blogSeo.posts.writeWithAi', 'Escrever com IA')}
              </Button>
            </div>
            <RichTextEditor
              value={form.content}
              onChange={(html) => update('content', html)}
              placeholder={t('blogSeo.posts.contentPlaceholder', "Escreva o artigo, ou clique em 'Escrever com IA'.")}
            />
          </div>
          <Textarea
            label={t('blogSeo.posts.excerptLabel', 'Resumo (excerpt)')}
            value={form.excerpt}
            onChange={(e) => update('excerpt', e.target.value)}
            placeholder={t('blogSeo.posts.excerptPlaceholder', 'Breve resumo do post')}
          />
          <div className="space-y-2">
            <ImageUpload
              label={t('blogSeo.posts.featuredImageLabel', 'Imagem destacada')}
              value={form.featured_image_url}
              onChange={(url) => update('featured_image_url', url)}
            />
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Sparkles className="w-3.5 h-3.5" />}
              onClick={handleGenerateImage}
              isLoading={genImg}
            >
              {genImg
                ? t('blogSeo.posts.generatingImage', 'Gerando imagem...')
                : t('blogSeo.posts.generateImageAi', 'Gerar imagem com IA')}
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('blogSeo.posts.categoriesLabel', 'Categorias')}
              value={form.categories}
              onChange={(e) => update('categories', e.target.value)}
              helperText={t('blogSeo.posts.commaSeparated', 'Separadas por vírgula')}
              placeholder="Marketing, SEO"
            />
            <Input
              label={t('blogSeo.posts.tagsLabel', 'Tags')}
              value={form.tags}
              onChange={(e) => update('tags', e.target.value)}
              helperText={t('blogSeo.posts.commaSeparated', 'Separadas por vírgula')}
              placeholder={t('blogSeo.posts.tagsPlaceholder', 'dicas, 2026')}
            />
          </div>
        </div>

        {/* RIGHT: sidebar */}
        <div className="space-y-4">
          {/* Publicação */}
          <Card className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">{t('blogSeo.posts.publicationHeading', 'Publicação')}</h4>
            <Select
              label={t('blogSeo.posts.connectionLabel', 'Conexão')}
              options={connectionOptions}
              value={form.connection_id}
              onChange={(e) => update('connection_id', e.target.value)}
            />
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.target.checked)}
              />
              <span className="text-sm text-gray-900 dark:text-white">
                {t('blogSeo.posts.scheduleToggle', 'Agendar publicação')}
              </span>
            </label>
            {scheduleEnabled && (
              <div>
                <label className="label">{t('blogSeo.posts.scheduleDateLabel', 'Data de agendamento')}</label>
                <input
                  type="datetime-local"
                  className="input w-full"
                  value={form.scheduled_at}
                  onChange={(e) => update('scheduled_at', e.target.value)}
                />
              </div>
            )}
          </Card>

          {/* SEO meta */}
          <Card className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">{t('blogSeo.posts.seoHeading', 'SEO')}</h4>
            <div>
              <Input
                label={t('blogSeo.posts.metaTitleLabel', 'Meta título')}
                value={form.metaTitle}
                onChange={(e) => update('metaTitle', e.target.value)}
                placeholder={t('blogSeo.posts.metaTitlePlaceholder', 'Título para mecanismos de busca')}
              />
              <p className={`mt-1 text-xs ${metaTitleOk ? 'text-green-500' : 'text-gray-500 dark:text-gray-400'}`}>
                {t('blogSeo.posts.metaTitleChars', '{{count}} caracteres (ideal 30-65)', { count: metaTitleLen })}
              </p>
            </div>
            <div>
              <Textarea
                label={t('blogSeo.posts.metaDescriptionLabel', 'Meta descrição')}
                value={form.metaDescription}
                onChange={(e) => update('metaDescription', e.target.value)}
                placeholder={t('blogSeo.posts.metaDescriptionPlaceholder', 'Descrição para mecanismos de busca')}
              />
              <p className={`mt-1 text-xs ${metaDescOk ? 'text-green-500' : 'text-gray-500 dark:text-gray-400'}`}>
                {t('blogSeo.posts.metaDescChars', '{{count}} caracteres (ideal 120-160)', { count: metaDescLen })}
              </p>
            </div>
            <Input
              label={t('blogSeo.posts.focusKeywordLabel', 'Palavra-chave foco')}
              value={form.focusKeyword}
              onChange={(e) => update('focusKeyword', e.target.value)}
              placeholder={t('blogSeo.posts.focusKeywordPlaceholder', 'ex: marketing digital')}
            />
            {/* Google SERP snippet preview */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
              <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{t('blogSeo.posts.googlePreview', 'Prévia no Google')}</p>
              <p className="text-[#1a0dab] dark:text-blue-400 text-sm leading-snug truncate">
                {form.metaTitle || form.title || t('blogSeo.posts.titlePlaceholder', 'Título do post')}
              </p>
              <p className="text-[#006621] dark:text-green-500 text-xs truncate">
                seusite.com › {form.slug || t('blogSeo.posts.slugFallback', 'slug-do-post')}
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-xs line-clamp-2">
                {form.metaDescription || form.excerpt || t('blogSeo.posts.metaDescPreviewFallback', 'A meta descrição aparecerá aqui — escreva entre 120 e 160 caracteres.')}
              </p>
            </div>
          </Card>

          {/* Live SEO analysis */}
          <Card className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900 dark:text-white">{t('blogSeo.posts.seoAnalysisHeading', 'Análise SEO')}</h4>
              {analyzing && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            </div>
            {analysis ? (
              <>
                <div className="flex items-center gap-4">
                  <ScoreCircle score={analysis.score} />
                  <div className="text-sm space-y-1">
                    <p className="text-gray-500 dark:text-gray-400">
                      {t('blogSeo.posts.wordsLabel', 'Palavras')}: <span className="text-gray-900 dark:text-white font-medium">{analysis.wordCount}</span>
                    </p>
                    <p className="text-gray-500 dark:text-gray-400">
                      {t('blogSeo.posts.densityLabel', 'Densidade')}:{' '}
                      <span className="text-gray-900 dark:text-white font-medium">
                        {analysis.keywordDensity?.toFixed(1)}%
                      </span>
                    </p>
                  </div>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {analysis.checks?.map((c) => (
                    <SeoCheckItem key={c.id} check={c} />
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('blogSeo.posts.analysisEmptyState', 'Comece a escrever para ver a análise SEO em tempo real.')}
              </p>
            )}
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="ghost" onClick={onClose} disabled={saving || publishing}>
          {t('blogSeo.posts.cancel', 'Cancelar')}
        </Button>
        <Button variant="secondary" onClick={handleSave} isLoading={saving} disabled={publishing}>
          {t('blogSeo.posts.save', 'Salvar')}
        </Button>
        <Button
          variant="primary"
          leftIcon={<Send className="w-4 h-4" />}
          onClick={handlePublishNow}
          isLoading={publishing}
          disabled={saving || !post || !form.connection_id}
        >
          {t('blogSeo.posts.publishNow', 'Publicar agora')}
        </Button>
      </div>
    </Modal>
  );
};

export default BlogPostEditor;
